import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { FileText, Book, Scale, Globe, Ship, ShieldCheck, ArrowRight, Search, ExternalLink, Building2, DollarSign, FileCheck, Gavel, AlertCircle, Package } from 'lucide-react';

const Resources = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const resourceCategories = [
    {
      id: 'us-export-controls',
      icon: ShieldCheck,
      title: 'U.S. Export Controls',
      description: 'ITAR, EAR, and export licensing requirements for controlled items',
      resources: [
        {
          title: 'Department of State - Directorate of Defense Trade Controls (DDTC)',
          url: 'https://www.pmddtc.state.gov/ddtc_public',
          description: 'Responsible for ITAR-controlled defense articles and services'
        },
        {
          title: 'International Traffic in Arms Regulations (ITAR)',
          url: 'https://www.pmddtc.state.gov/ddtc_public?id=ddtc_kb_article_page&sys_id=24d528fddbfc930044f9ff621f961987',
          description: 'Complete ITAR regulations and guidance'
        },
        {
          title: 'Bureau of Industry and Security (BIS)',
          url: 'https://www.bis.doc.gov/',
          description: 'Commerce Department division administering Export Administration Regulations'
        },
        {
          title: 'Export Administration Regulations (EAR)',
          url: 'https://www.bis.doc.gov/index.php/regulations/export-administration-regulations-ear',
          description: 'Complete EAR regulations for dual-use items'
        },
        {
          title: 'Export Control Classification Number (ECCN) Guide',
          url: 'https://www.bis.doc.gov/index.php/licensing/commerce-control-list-classification/export-control-classification-number-eccn',
          description: 'Determine ECCN classifications for dual-use exports'
        },
        {
          title: 'Commodity Jurisdiction (CJ) Final Determinations',
          url: 'https://www.pmddtc.state.gov/ddtc_public?id=ddtc_kb_article_page&sys_id=6ea6afdcdbc36300529d368d7c96194b',
          description: 'Database of CJ determinations for USML vs. CCL classification'
        },
        {
          title: 'Re-export Guidance',
          url: 'https://www.bis.doc.gov/index.php/licensing/reexports-and-offshore-transactions',
          description: 'Rules for re-exports and offshore transactions'
        },
        {
          title: 'Encryption and EAR',
          url: 'https://www.bis.doc.gov/index.php/policy-guidance/encryption',
          description: 'Export controls specific to encryption technology'
        },
        {
          title: 'Defense Technology Security Administration (DTSA)',
          url: 'https://www.dtsa.mil/SitePages/default.aspx',
          description: 'DoD technology security policies and international transfers'
        }
      ]
    },
    {
      id: 'tariff-customs',
      icon: Building2,
      title: 'Tariff & Customs',
      description: 'Import/export duties, HTS classification, and customs procedures',
      resources: [
        {
          title: 'U.S. Harmonized Tariff Schedule (HTS) - Online Search',
          url: 'https://hts.usitc.gov/',
          description: 'Official searchable database for U.S. import tariff classifications'
        },
        {
          title: 'U.S. Customs and Border Protection (CBP)',
          url: 'https://www.cbp.gov/',
          description: 'Primary U.S. border control and customs agency'
        },
        {
          title: 'Customs Rulings Online Search System (CROSS)',
          url: 'https://rulings.cbp.gov/',
          description: 'Searchable database of CBP classification and ruling decisions'
        },
        {
          title: 'Schedule B Export Codes Search',
          url: 'https://www.census.gov/foreign-trade/schedules/b/index.html',
          description: '10-digit codes for classifying U.S. exports'
        },
        {
          title: 'Country-Specific Tariff Information',
          url: 'https://www.trade.gov/country-commercial-guides',
          description: 'Tariff rates and tax information by destination country'
        },
        {
          title: 'Automated Broker Interface (ABI)',
          url: 'https://www.cbp.gov/trade/acs/abi/contact-info',
          description: 'System for electronic customs entry filing'
        },
        {
          title: 'Automated Commercial Environment (ACE)',
          url: 'https://www.cbp.gov/trade/automated',
          description: 'CBP\'s primary system for trade processing and compliance'
        },
        {
          title: 'World Customs Organization',
          url: 'https://www.wcoomd.org/en.aspx',
          description: 'International standards for customs procedures and classification'
        },
        {
          title: 'ASEAN Tariff Finder',
          url: 'https://tariff-finder.asean.org/',
          description: 'Search tariffs across ASEAN member countries'
        },
        {
          title: 'European Union Customs Database',
          url: 'https://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp',
          description: 'EU TARIC database for import duties and measures'
        }
      ]
    },
    {
      id: 'trade-sanctions',
      icon: AlertCircle,
      title: 'Trade Sanctions & Screening',
      description: 'OFAC sanctions programs and denied party screening',
      resources: [
        {
          title: 'Office of Foreign Assets Control (OFAC)',
          url: 'https://www.treasury.gov/resource-center/sanctions/Pages/default.aspx',
          description: 'U.S. Treasury sanctions programs and enforcement'
        },
        {
          title: 'OFAC Specially Designated Nationals (SDN) List',
          url: 'https://sanctionssearch.ofac.treas.gov/',
          description: 'Searchable database of blocked persons and entities'
        },
        {
          title: 'BIS Denied Persons List',
          url: 'https://www.bis.doc.gov/index.php/policy-guidance/lists-of-parties-of-concern',
          description: 'Consolidated screening list for export compliance'
        },
        {
          title: 'Consolidated Screening List',
          url: 'https://www.trade.gov/consolidated-screening-list',
          description: 'Combined list of all U.S. government restricted parties'
        },
        {
          title: 'Sanctions Programs and Country Information',
          url: 'https://home.treasury.gov/policy-issues/financial-sanctions/sanctions-programs-and-country-information',
          description: 'Detailed information on country-specific sanctions'
        }
      ]
    },
    {
      id: 'trade-agreements',
      icon: Globe,
      title: 'U.S. Free Trade Agreements',
      description: 'FTA qualification, rules of origin, and preferential treatment',
      resources: [
        {
          title: 'USTR - Free Trade Agreements',
          url: 'https://ustr.gov/trade-agreements/free-trade-agreements',
          description: 'Official source for all U.S. FTA texts and information'
        },
        {
          title: 'USMCA (United States-Mexico-Canada Agreement)',
          url: 'https://ustr.gov/trade-agreements/free-trade-agreements/united-states-mexico-canada-agreement',
          description: 'Complete USMCA agreement text and implementation guidance'
        },
        {
          title: 'Rules of Origin Overview',
          url: 'https://www.cbp.gov/trade/priority-issues/trade-agreements/free-trade-agreements',
          description: 'CBP guidance on FTA origin determinations'
        },
        {
          title: 'Certificate of Origin Requirements',
          url: 'https://www.trade.gov/certificates-origin',
          description: 'Documentation requirements for claiming FTA benefits'
        },
        {
          title: 'FTA Tariff Tool',
          url: 'https://ftatarifftool.usitc.gov/',
          description: 'Calculate duty rates under various FTAs'
        },
        {
          title: 'Generalized System of Preferences (GSP)',
          url: 'https://ustr.gov/issue-areas/preference-programs/generalized-system-preferences-gsp',
          description: 'Preferential duty-free treatment for developing countries'
        }
      ]
    },
    {
      id: 'incoterms',
      icon: Ship,
      title: 'Incoterms & Shipping',
      description: 'International commercial terms and shipping responsibilities',
      resources: [
        {
          title: 'Incoterms® 2020 Official Rules',
          url: 'https://iccwbo.org/resources-for-business/incoterms-rules/',
          description: 'International Chamber of Commerce official Incoterms publication'
        },
        {
          title: 'Incoterms Overview - Investopedia',
          url: 'https://www.investopedia.com/terms/i/incoterms.asp',
          description: 'Plain-language explanation of all Incoterms rules'
        },
        {
          title: 'EXW - Ex Works',
          url: 'https://www.investopedia.com/terms/e/exw.asp',
          description: 'Seller makes goods available at their premises'
        },
        {
          title: 'FCA - Free Carrier',
          url: 'https://www.investopedia.com/terms/f/fca.asp',
          description: 'Seller delivers to carrier nominated by buyer'
        },
        {
          title: 'FOB - Free On Board',
          url: 'https://www.investopedia.com/terms/f/fob.asp',
          description: 'Seller loads goods on vessel nominated by buyer'
        },
        {
          title: 'CIF - Cost, Insurance and Freight',
          url: 'https://www.investopedia.com/ask/answers/072815/what-difference-between-cost-and-freight-cfr-and-cost-insurance-and-freight-cif.asp',
          description: 'Seller pays freight and insurance to destination port'
        },
        {
          title: 'DDP - Delivered Duty Paid',
          url: 'https://www.investopedia.com/terms/d/delivery-duty-paid.asp',
          description: 'Seller responsible for all costs including import duties'
        }
      ]
    },
    {
      id: 'shipping-docs',
      icon: FileText,
      title: 'Shipping Documents',
      description: 'Commercial invoices, packing lists, and export documentation',
      resources: [
        {
          title: 'Electronic Export Information (EEI) Filing - Census',
          url: 'https://www.census.gov/foreign-trade/aes/ace-aesdirect.html',
          description: 'Official AES/AESDirect filing portal and user guides'
        },
        {
          title: 'ACE Secure Data Portal',
          url: 'https://ace.cbp.dhs.gov/',
          description: 'CBP Automated Commercial Environment for trade processing'
        },
        {
          title: 'Import Documentation Requirements - CBP',
          url: 'https://www.help.cbp.gov/s/article/Article-1162?language=en_US',
          description: 'Official CBP guide for commercial invoices and import docs'
        },
        {
          title: 'Export Documentation Guide - Trade.gov',
          url: 'https://www.trade.gov/common-export-documents',
          description: 'Common export documents including bills of lading'
        },
        {
          title: 'Certificate of Origin - Trade.gov',
          url: 'https://www.trade.gov/special-documents',
          description: 'How to obtain COOs for exports and FTA certifications'
        },
        {
          title: 'FTA Certificates of Origin',
          url: 'https://www.trade.gov/fta-certificates-origin',
          description: 'Free trade agreement certification requirements'
        }
      ]
    },
    {
      id: 'international-tax',
      icon: DollarSign,
      title: 'International Tax (VAT)',
      description: 'Value-added tax and international tax considerations',
      resources: [
        {
          title: 'OECD - International Tax',
          url: 'https://www.oecd.org/tax/',
          description: 'Global tax policy and international standards'
        },
        {
          title: 'European Commission - VAT',
          url: 'https://taxation-customs.ec.europa.eu/taxation/vat_en',
          description: 'EU VAT rules and regulations'
        },
        {
          title: 'IRS - International Taxpayers',
          url: 'https://www.irs.gov/businesses/international-businesses',
          description: 'U.S. tax obligations for international business'
        },
        {
          title: 'VAT Rates by Country',
          url: 'https://www.avalara.com/vatlive/en/vat-rates.html',
          description: 'Current VAT rates worldwide'
        }
      ]
    },
    {
      id: 'trade-finance',
      icon: DollarSign,
      title: 'Trade Finance',
      description: 'Letters of credit, payment terms, and trade financing options',
      resources: [
        {
          title: 'Export-Import Bank of the United States',
          url: 'https://www.exim.gov/',
          description: 'Official U.S. export credit agency'
        },
        {
          title: 'International Trade Administration - Trade Finance',
          url: 'https://www.trade.gov/trade-finance-guide',
          description: 'Guide to trade finance methods and best practices'
        },
        {
          title: 'ICC - Letters of Credit',
          url: 'https://iccwbo.org/business-solutions/incoterms-rules/letters-of-credit/',
          description: 'International standards for documentary credits'
        },
        {
          title: 'SWIFT - Trade Finance',
          url: 'https://www.swift.com/your-needs/trade-finance',
          description: 'Global messaging standards for trade finance'
        }
      ]
    },
    {
      id: 'international-trade-law',
      icon: Gavel,
      title: 'International Trade Law',
      description: 'WTO agreements, trade remedies, and international regulations',
      resources: [
        {
          title: 'World Trade Organization (WTO)',
          url: 'https://www.wto.org/',
          description: 'International trade rules and dispute settlement'
        },
        {
          title: 'WTO Trade Agreements',
          url: 'https://www.wto.org/english/docs_e/legal_e/legal_e.htm',
          description: 'Complete WTO legal texts and agreements'
        },
        {
          title: 'U.S. International Trade Commission',
          url: 'https://www.usitc.gov/',
          description: 'Trade remedy investigations and tariff information'
        },
        {
          title: 'UNCITRAL - International Trade Law',
          url: 'https://uncitral.un.org/',
          description: 'UN Commission on International Trade Law'
        }
      ]
    },
    {
      id: 'us-trade-law',
      icon: Scale,
      title: 'U.S. Trade Law',
      description: 'Domestic trade statutes, regulations, and enforcement',
      resources: [
        {
          title: 'U.S. Code Title 19 - Customs Duties',
          url: 'https://uscode.house.gov/view.xhtml?path=/prelim@title19&edition=prelim',
          description: 'Complete U.S. customs and trade law'
        },
        {
          title: 'Code of Federal Regulations - Title 19',
          url: 'https://www.ecfr.gov/current/title-19',
          description: 'Federal regulations governing customs and trade'
        },
        {
          title: 'U.S. Trade Representative',
          url: 'https://ustr.gov/',
          description: 'Official U.S. trade policy and negotiations'
        },
        {
          title: 'Federal Register - Trade Notices',
          url: 'https://www.federalregister.gov/',
          description: 'Daily publication of proposed and final trade regulations'
        }
      ]
    },
    {
      id: 'eu-export-controls',
      icon: ShieldCheck,
      title: 'EU Export Controls',
      description: 'European Union dual-use and military export regulations',
      resources: [
        {
          title: 'European Commission - Export Control',
          url: 'https://policy.trade.ec.europa.eu/enforcement-and-protection/export-controls_en',
          description: 'Official EU export control policy and regulations'
        },
        {
          title: 'EU Dual-Use Regulation 2021/821',
          url: 'https://eur-lex.europa.eu/eli/reg/2021/821/oj',
          description: 'Current EU dual-use export control regulation'
        },
        {
          title: 'EU Common Military List',
          url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A52022XG0309%2801%29',
          description: 'List of military equipment covered by EU controls'
        }
      ]
    },
    {
      id: 'logistics',
      icon: Package,
      title: 'Logistics & Supply Chain',
      description: 'Freight forwarding, warehousing, and supply chain management',
      resources: [
        {
          title: 'International Air Transport Association (IATA)',
          url: 'https://www.iata.org/',
          description: 'Air cargo standards and regulations'
        },
        {
          title: 'Federal Maritime Commission',
          url: 'https://www.fmc.gov/',
          description: 'U.S. ocean shipping regulation'
        },
        {
          title: 'National Customs Brokers & Forwarders Association',
          url: 'https://www.ncbfaa.org/',
          description: 'Professional association for customs brokers and freight forwarders'
        },
        {
          title: 'Council of Supply Chain Management Professionals',
          url: 'https://cscmp.org/',
          description: 'Supply chain management best practices and resources'
        }
      ]
    },
    {
      id: 'import-export-guides',
      icon: Book,
      title: 'Import & Export Guides',
      description: 'Comprehensive guides for international trade operations',
      resources: [
        {
          title: 'Learn How to Export - Trade.gov',
          url: 'https://www.trade.gov/learn-how-export',
          description: 'Step-by-step export roadmap and planning tools'
        },
        {
          title: 'A Basic Guide to Exporting (PDF)',
          url: 'https://legacy.trade.gov/guide_to_exporting.pdf',
          description: 'Comprehensive beginner guide to exporting'
        },
        {
          title: 'Country Commercial Guides',
          url: 'https://www.trade.gov/country-commercial-guides',
          description: 'Market intelligence and trade guides for 140+ countries'
        },
        {
          title: 'Export Compliance Training - BIS',
          url: 'https://www.bis.doc.gov/index.php/about-bis/newsroom/events',
          description: 'BIS seminars, webinars and export control training events'
        },
        {
          title: 'Export Compliance Programs - BIS',
          url: 'https://www.bis.doc.gov/index.php/compliance-a-training',
          description: 'Guidelines for building effective compliance programs'
        }
      ]
    },
    {
      id: 'trade-terms',
      icon: FileCheck,
      title: 'Trade Terms & Glossary',
      description: 'Definitions and explanations of international trade terminology',
      resources: [
        {
          title: 'Thesaurus of International Trade Terms - Trade.gov',
          url: 'https://www.trade.gov/data-visualization/thesaurus-international-trade-terms',
          description: 'Official ITA structured trade terminology'
        },
        {
          title: 'Glossary of Trade Terms - Trade.gov',
          url: 'https://beta.trade.gov/article?id=Glossary-of-Trade-Terms',
          description: 'Comprehensive glossary from Basic Guide to Exporting'
        },
        {
          title: 'Know Your Incoterms - Trade.gov',
          url: 'https://www.trade.gov/know-your-incoterms',
          description: 'Definitions of international commercial terms'
        },
        {
          title: 'WTO Glossary',
          url: 'https://www.wto.org/english/thewto_e/glossary_e/glossary_e.htm',
          description: 'World Trade Organization terminology'
        }
      ]
    },
    {
      id: 'trade-data',
      icon: Building2,
      title: 'International Trade Data',
      description: 'Statistics, market research, and trade analytics',
      resources: [
        {
          title: 'U.S. Census Bureau - Trade Data',
          url: 'https://www.census.gov/foreign-trade/data/index.html',
          description: 'Official U.S. import and export statistics'
        },
        {
          title: 'USA Trade Online',
          url: 'https://usatrade.census.gov/',
          description: 'Searchable database of U.S. trade statistics'
        },
        {
          title: 'UN Comtrade Database',
          url: 'https://comtradeplus.un.org/',
          description: 'Global trade statistics by country and commodity'
        },
        {
          title: 'World Bank - Trade Data',
          url: 'https://data.worldbank.org/topic/trade',
          description: 'International trade indicators and analysis'
        },
        {
          title: 'OECD Trade Statistics',
          url: 'https://www.oecd.org/trade/topics/trade-statistics/',
          description: 'Trade data and analysis for OECD countries'
        }
      ]
    },
    {
      id: 'international-trade-agreements',
      icon: Globe,
      title: 'International Trade Agreements',
      description: 'Multilateral and regional trade agreements worldwide',
      resources: [
        {
          title: 'Regional Trade Agreements Information System',
          url: 'https://rtais.wto.org/UI/PublicMaintainRTAHome.aspx',
          description: 'WTO database of regional trade agreements'
        },
        {
          title: 'Asia-Pacific Economic Cooperation (APEC)',
          url: 'https://www.apec.org/',
          description: 'Economic forum for Asia-Pacific region'
        },
        {
          title: 'Association of Southeast Asian Nations (ASEAN)',
          url: 'https://asean.org/',
          description: 'Regional organization promoting economic integration'
        },
        {
          title: 'African Continental Free Trade Area (AfCFTA)',
          url: 'https://au-afcfta.org/',
          description: 'Pan-African free trade agreement'
        },
        {
          title: 'Comprehensive and Progressive Trans-Pacific Partnership',
          url: 'https://www.international.gc.ca/trade-commerce/trade-agreements-accords-commerciaux/agr-acc/cptpp-ptpgp/index.aspx',
          description: 'Major Asia-Pacific trade agreement'
        }
      ]
    }
  ];

  // Filter categories based on search
  const filteredCategories = resourceCategories.filter(category => {
    const matchesSearch = searchQuery === '' || 
      category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.resources.some(r => r.title.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = activeCategory === 'all' || category.id === activeCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="resources-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Trade Compliance<br />Resource Center
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Comprehensive collection of regulatory resources, government links, and essential tools for international trade professionals.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search resources, regulations, or topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-base"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-700 mb-2">16</div>
              <div className="text-sm text-gray-600">Resource Categories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-700 mb-2">120+</div>
              <div className="text-sm text-gray-600">Official Links</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-700 mb-2">50+</div>
              <div className="text-sm text-gray-600">Government Agencies</div>
            </div>
          </div>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse by Category</h2>
            <p className="text-base sm:text-lg text-gray-600">
              {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'} available
            </p>
          </div>

          {/* Category Cards with Expandable Links */}
          <div className="space-y-6">
            {filteredCategories.map((category) => {
              const Icon = category.icon;
              return (
                <div key={category.id} className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all">
                  {/* Category Header */}
                  <div className="p-8">
                    <div className="flex items-start gap-6">
                      <div className="w-14 h-14 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="h-7 w-7 text-slate-700" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{category.title}</h3>
                        <p className="text-gray-600 leading-relaxed mb-6">{category.description}</p>
                        
                        {/* Resource Links */}
                        <div className="space-y-3">
                          {category.resources.map((resource, idx) => (
                            <a
                              key={idx}
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-start gap-3 p-4 rounded-lg hover:bg-slate-50 transition-colors group"
                            >
                              <ExternalLink className="h-4 w-4 text-slate-500 mt-1 flex-shrink-0 group-hover:text-slate-700" />
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 group-hover:text-slate-700 mb-1">
                                  {resource.title}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {resource.description}
                                </div>
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredCategories.length === 0 && (
            <div className="text-center py-20">
              <div className="text-gray-400 mb-4">
                <Search className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No resources found</h3>
              <p className="text-gray-600">Try adjusting your search terms</p>
            </div>
          )}
        </div>
      </section>

      {/* Helpful Resources Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Helpful Quick Start Guides</h2>
            <p className="text-base sm:text-lg text-gray-600">
              Direct links to essential compliance guides and tools
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <a 
              href="https://www.bis.doc.gov/index.php/documents/new-to-export-controls/2319-export-control-basics/file"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-slate-50 border border-slate-200 rounded-lg p-8 hover:shadow-lg transition-all group"
            >
              <div className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3">
                BIS GUIDE
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-slate-700">Export Control Basics</h3>
              <p className="text-sm text-gray-600 mb-4">
                Comprehensive guide on determining if you need an export license, ECCN classification, and compliance steps
              </p>
              <div className="flex items-center text-slate-700 font-semibold text-sm">
                Visit Guide <ExternalLink className="ml-2 h-4 w-4" />
              </div>
            </a>

            <a 
              href="https://hts.usitc.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-slate-50 border border-slate-200 rounded-lg p-8 hover:shadow-lg transition-all group"
            >
              <div className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3">
                USITC TOOL
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-slate-700">HTS Classification Search</h3>
              <p className="text-sm text-gray-600 mb-4">
                Official U.S. Harmonized Tariff Schedule database for finding the correct classification and duty rates
              </p>
              <div className="flex items-center text-slate-700 font-semibold text-sm">
                Search HTS <ExternalLink className="ml-2 h-4 w-4" />
              </div>
            </a>

            <a 
              href="https://www.trade.gov/know-your-incoterms"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-slate-50 border border-slate-200 rounded-lg p-8 hover:shadow-lg transition-all group"
            >
              <div className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3">
                TRADE.GOV GUIDE
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-slate-700">Know Your Incoterms</h3>
              <p className="text-sm text-gray-600 mb-4">
                Official guide to understanding and choosing the right Incoterms for your international shipments
              </p>
              <div className="flex items-center text-slate-700 font-semibold text-sm">
                Learn Incoterms <ExternalLink className="ml-2 h-4 w-4" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Need Expert Guidance?
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Our experienced trade compliance professionals can provide tailored advice for your specific import/export challenges.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule a Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resources;